'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { PortableText } from '@portabletext/react';

import { resolvePageLink } from '@/sanity/globals/CTA';
import type { IFooter } from '@/sanity/lib/definitions';
import { richTextComponent } from '@/sanity/sanityPortableText';

import logo from '@/public/riz-affiliate/final-footer-logo.svg';
import waveDots from '@/public/fabio-affiliate/noise.jpg';

export default function FooterSection({
  data,
  language,
}: {
  data: IFooter;
  language?: string;
}) {
  const legalLinks = data?.navColumns?.length
    ? data.navColumns[data.navColumns.length - 1].links ?? []
    : [];

  return (
    <footer className="flex relative flex-col items-center w-full overflow-clip bg-white px-[3.875rem] pt-[4.75rem] pb-[5.875rem] lg:px-[50rem] lg:pt-[4rem] lg:pb-[4rem]">
      <Image
        src={waveDots}
        alt=""
        className="absolute inset-0 object-cover !w-full !h-full mix-blend-exclusion opacity-10 pointer-events-none"
      />

      <div className="flex relative flex-col items-center w-full gap-[2.25rem] lg:flex-row lg:justify-between lg:gap-0">
        <Link href="/" className="order-1 lg:order-2 shrink-0">
          <Image
            alt="IQ Capital"
            src={logo}
            className="w-[18.25rem] h-auto lg:w-[19.375rem]"
          />
        </Link>

        {legalLinks.length > 0 && (
          <p className="m-0 order-2 text-center font-geist text-black/[79%] text-[2rem] leading-[3rem] lg:order-3 lg:flex-1 lg:text-right">
            {legalLinks.map((item, index) => (
              <React.Fragment key={index}>
                <Link
                  className="transition-opacity hover:opacity-70"
                  href={resolvePageLink(item.link, language) ?? '/'}
                  target={item.link?.openInNewTab ? '_blank' : '_self'}
                >
                  {item.title}
                </Link>
                {index < legalLinks.length - 1 && ' / '}
              </React.Fragment>
            ))}
          </p>
        )}

        <p className="m-0 order-3 text-center font-geist text-black/[79%] text-[2rem] leading-[3rem] lg:order-1 lg:flex-1 lg:text-left">
          © IQ Capital - All rights reserved
        </p>
      </div>

      {data?.disclaimer && (
        <div className="relative w-[min(45.9375rem,100%)] border-t border-black/20 mt-[2.25rem] pt-[2.25rem] lg:w-full lg:mt-[3rem] lg:py-[4rem]">
          <div className="richtext richtext-center mx-auto w-[min(44rem,100%)] gap-[2.625rem] [text-wrap:wrap] prose-p:font-geist prose-p:text-black/50 prose-p:text-[1.75rem] prose-p:leading-[1.5] lg:w-full">
            <PortableText
              value={data.disclaimer}
              components={richTextComponent}
            />
          </div>
        </div>
      )}
    </footer>
  );
}
